import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  BankOutlined,
  CarOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const SIDE_ICONS = [
  <BankOutlined />,
  <HomeOutlined />,
  <TeamOutlined />,
  <EnvironmentOutlined />,
  <CarOutlined />,
];

/** PCB-style path: horizontal → 45° corner → vertical → 45° corner → horizontal */
const buildCircuitPath = (from, to) => {
  const midX = from.x + (to.x - from.x) * 0.52;
  const dy = to.y - from.y;

  if (Math.abs(dy) < 3) {
    return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  }

  const r = Math.min(14, Math.abs(dy) * 0.35, Math.abs(to.x - from.x) * 0.12);
  const sy = dy > 0 ? 1 : -1;

  return [
    `M ${from.x} ${from.y}`,
    `L ${midX - r} ${from.y}`,
    `L ${midX} ${from.y + r * sy}`,
    `L ${midX} ${to.y - r * sy}`,
    `L ${midX + r} ${to.y}`,
    `L ${to.x} ${to.y}`,
  ].join(" ");
};

const trimPoint = (from, to, amount) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  return {
    x: to.x - (dx / len) * amount,
    y: to.y - (dy / len) * amount,
  };
};

const CircuitLink = ({ path, kind, index }) => {
  const { pathId, d, from, to } = path;
  const isOut = kind === "out";
  const isIn = kind === "in";
  const cometDur = isOut || isIn ? "1.1s" : "1.8s";
  const cometDelay = 0.15 + index * 0.18;

  return (
    <g
      className={`mfm-circuit mfm-circuit--${kind}`}
      style={{ "--pulse-delay": `${index * 0.15}s` }}
    >
      <path id={pathId} className="mfm-circuit__wire" d={d} pathLength="1" />
      <path className="mfm-circuit__pulse" d={d} pathLength="1" />
      {/* Start: hub (out) or district (in) */}
      <circle
        className="mfm-circuit__dot mfm-circuit__dot--start"
        cx={from.x}
        cy={from.y}
        r="2"
      />
      {/* End: district (out) or hub (in) */}
      <circle
        className="mfm-circuit__dot mfm-circuit__dot--end"
        cx={to.x}
        cy={to.y}
        r="2.5"
      />
      <circle className="mfm-circuit__comet" r="2">
        <animateMotion
          dur={cometDur}
          repeatCount="indefinite"
          begin={`${cometDelay}s`}
          calcMode="linear"
        >
          <mpath href={`#${pathId}`} xlinkHref={`#${pathId}`} />
        </animateMotion>
      </circle>
      {(isOut || isIn) && (
        <circle className="mfm-circuit__comet mfm-circuit__comet--trail" r="1.5">
          <animateMotion
            dur={cometDur}
            repeatCount="indefinite"
            begin={`${cometDelay + 0.4}s`}
            calcMode="linear"
          >
            <mpath href={`#${pathId}`} xlinkHref={`#${pathId}`} />
          </animateMotion>
        </circle>
      )}
    </g>
  );
};

const FlowCard = ({ item, kind, index, innerRef, onClick }) => {
  const count =
    kind === "out"
      ? item.outsider_count
      : item.inside_count ?? item.insider_count ?? item.outsider_count;
  const name =
    item.district_name ?? item.DistrictName ?? item.districtName ?? "—";

  return (
    <button
      type="button"
      ref={innerRef}
      className={`mfm-card mfm-card--${kind}`}
      style={{ "--card-i": `${index * 70}ms` }}
      onClick={() => onClick?.(kind, item)}
    >
      <span className="mfm-card__icon" aria-hidden>
        {SIDE_ICONS[index % SIDE_ICONS.length]}
      </span>
      <span className="mfm-card__body">
        <span className="mfm-card__name">{name}</span>
        <span className={`mfm-card__count mfm-card__count--${kind}`}>
          {count ?? 0}
        </span>
      </span>
    </button>
  );
};

const MovementFlowMap = ({
  hubDistrict,
  hubCount,
  hubTitle = "हद्दीत राहणारे व हद्दीत गुन्हे करणारे",
  outsiders = [],
  insiders = [],
  onNodeClick,
}) => {
  const stageRef = useRef(null);
  const hubRef = useRef(null);
  const outRefs = useRef([]);
  const inRefs = useRef([]);
  const [paths, setPaths] = useState({ out: [], inn: [] });
  const [ready, setReady] = useState(false);

  const measurePaths = useCallback(() => {
    const stage = stageRef.current;
    const hub = hubRef.current;
    if (!stage || !hub) return;

    const s = stage.getBoundingClientRect();
    const h = hub.getBoundingClientRect();
    const hubLeft = {
      x: h.left - s.left,
      y: h.top - s.top + h.height / 2,
    };
    const hubRight = {
      x: h.right - s.left,
      y: hubLeft.y,
    };

    const pad = 8;

    /* बाहेर जाणारे: hub → left district cards */
    const outPaths = [];
    outRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const hubRaw = { x: h.left - s.left - 2, y: hubLeft.y };
      const cardRaw = {
        x: r.right - s.left + 2,
        y: r.top - s.top + r.height / 2,
      };
      const from = trimPoint(cardRaw, hubRaw, pad);
      const to = trimPoint(hubRaw, cardRaw, pad);
      outPaths.push({
        id: `out-${i}`,
        pathId: `mfm-circuit-out-${i}`,
        d: buildCircuitPath(from, to),
        from,
        to,
      });
    });

    /* बाहेरून येणारे: right district cards → hub */
    const inPaths = [];
    inRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const hubRaw = { x: h.right - s.left + 2, y: hubRight.y };
      const cardRaw = {
        x: r.left - s.left - 2,
        y: r.top - s.top + r.height / 2,
      };
      const from = trimPoint(hubRaw, cardRaw, pad);
      const to = trimPoint(cardRaw, hubRaw, pad);
      inPaths.push({
        id: `in-${i}`,
        pathId: `mfm-circuit-in-${i}`,
        d: buildCircuitPath(from, to),
        from,
        to,
      });
    });

    setPaths({ out: outPaths, inn: inPaths });
    setReady(true);
  }, []);

  useLayoutEffect(() => {
    outRefs.current = outRefs.current.slice(0, outsiders.length);
    inRefs.current = inRefs.current.slice(0, insiders.length);
    setReady(false);

    const run = () => {
      measurePaths();
      requestAnimationFrame(measurePaths);
    };
    run();

    const stage = stageRef.current;
    if (!stage) return undefined;

    const ro = new ResizeObserver(run);
    ro.observe(stage);
    window.addEventListener("resize", run);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", run);
    };
  }, [measurePaths, outsiders, insiders]);

  const rowCount = Math.max(outsiders.length, insiders.length, 1);

  return (
    <div
      className={`mfm-ref${ready ? " mfm-ref--ready" : ""}`}
      ref={stageRef}
    >
      <svg className="mfm-ref__lines" aria-hidden>
        <defs>
          <filter id="mfm-glow-blue" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="mfm-glow-green" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {paths.out.map((p, i) => (
          <CircuitLink key={p.id} path={p} kind="out" index={i} />
        ))}
        {paths.inn.map((p, i) => (
          <CircuitLink key={p.id} path={p} kind="in" index={i} />
        ))}
      </svg>

      <div className="mfm-ref__grid" style={{ "--flow-rows": rowCount }}>
        <div className="mfm-ref__col mfm-ref__col--out">
          {outsiders.map((item, i) => (
            <FlowCard
              key={item.district != null ? `out-${item.district}` : `out-${i}`}
              item={item}
              kind="out"
              index={i}
              innerRef={(el) => {
                outRefs.current[i] = el;
              }}
              onClick={onNodeClick}
            />
          ))}
        </div>

        <div className="mfm-ref__col mfm-ref__col--hub">
          <div className="mfm-ref__hub" ref={hubRef}>
            <div className="mfm-ref__hub-count">{hubCount ?? 0}</div>
            <BankOutlined className="mfm-ref__hub-icon" aria-hidden />
            <span className="mfm-ref__hub-name">{hubDistrict || "—"}</span>
          </div>
        </div>

        <div className="mfm-ref__col mfm-ref__col--in">
          {insiders.map((item, i) => (
            <FlowCard
              key={item.district != null ? `in-${item.district}` : `in-${i}`}
              item={item}
              kind="in"
              index={i}
              innerRef={(el) => {
                inRefs.current[i] = el;
              }}
              onClick={onNodeClick}
            />
          ))}
        </div>
      </div>

      <p className="mfm-ref__local-label">{hubTitle}</p>
    </div>
  );
};

export default MovementFlowMap;
